import { useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  FileText,
  Image as ImageIcon,
  Link2,
} from "lucide-react";
import { API, api, formatApiError } from "@/lib/api";

const TYPES = [
  { key: "website", label: "Link Website", icon: Link2 },
  { key: "foto", label: "Foto", icon: ImageIcon },
  { key: "dokumen", label: "Dokumen", icon: FileText },
];

export default function LastYearPanel({ item, year, readOnly = false }) {
  const prevYear = year - 1;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  // Halaman tautan publik memakai endpoint publik (tanpa login)
  const subsPath = readOnly
    ? `/api/public/monev/kartu/${item.code}/submissions`
    : `/api/monev/${item.code}/submissions`;
  const fileBase = readOnly
    ? `${API}/api/public/monev/file`
    : `${API}/api/monev/file`;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data) {
      setBusy(true);
      try {
        setData(await api(`${subsPath}?year=${prevYear}`, {}, !readOnly));
      } catch (e) {
        toast(formatApiError(e.message));
        setOpen(false);
      } finally {
        setBusy(false);
      }
    }
  };

  const hasAny = data && TYPES.some(({ key }) => (data[key] || []).length > 0);

  return (
    <div className="mt-3" data-testid={`monev-lastyear-${item.code}`}>
      <button
        type="button"
        data-testid={`monev-lastyear-toggle-${item.code}`}
        onClick={toggle}
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 transition-colors duration-200 hover:text-[#1479BF]"
      >
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
        {open
          ? `Sembunyikan isian tahun ${prevYear}`
          : `Lihat isian tahun ${prevYear}`}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-dashed border-[#E3EBF3] bg-[#FBFDFF] p-3">
          {busy ? (
            <p
              data-testid={`monev-lastyear-loading-${item.code}`}
              className="text-xs text-slate-400"
            >
              Memuat isian tahun {prevYear}...
            </p>
          ) : !hasAny ? (
            <p
              data-testid={`monev-lastyear-empty-${item.code}`}
              className="text-xs text-slate-400"
            >
              Belum ada isian tahun {prevYear}.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {TYPES.map(({ key, label, icon: Icon }) => {
                const list = data[key] || [];
                if (!list.length) return null;
                return (
                  <div key={key}>
                    <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                      {label}
                    </p>
                    <ul className="mt-1.5 space-y-1.5">
                      {list.map((e) => (
                        <li key={e.id}>
                          {key === "website" ? (
                            <a
                              href={e.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block max-w-[220px] truncate text-xs font-medium text-[#1479BF] underline"
                            >
                              {e.url}
                            </a>
                          ) : key === "foto" ? (
                            <a
                              href={`${fileBase}/${e.id}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={`${fileBase}/${e.id}`}
                                alt={e.name}
                                className="h-12 w-12 rounded-lg object-cover"
                              />
                            </a>
                          ) : (
                            <a
                              href={`${fileBase}/${e.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1479BF] underline"
                            >
                              <FileText
                                aria-hidden="true"
                                className="h-3.5 w-3.5 shrink-0"
                              />
                              <span className="max-w-[150px] truncate">
                                {e.name}
                              </span>
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}